import CreateIcon from '@mui/icons-material/Create'
import MoreIcon from '@mui/icons-material/MoreHoriz'
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import axios from 'axios'
import config from 'config'
import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'
import { orderBy } from 'lodash'
import debounce from 'lodash/debounce'
import T from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import DotCell from 'components/atoms/DotCell'
import FilterSelect from 'components/atoms/FilterSelect'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'

import useEntityData from 'hooks/useEntityData'
import useQuery from 'hooks/useQuery'
import useTablePaginationSync from 'hooks/useTablePaginationSync'

import useSnackbar from '../Snackbar/useSnackbar'

import GroupAssignmentDialog from './GroupAssignmentDialog'
import RulesHeader from './RulesHeader'
import useRulesFilters from './useRulesFilters'

export const createGroups = (t) =>
  new Array(10).fill(1).map((_, index) => ({
    name: t('rules.grouping.group', { attribute: index + 1 }),
    id: index + 1,
  }))

const GroupHeaderRenderer = ({ data, column }) => {
  const checkColumn = Number(column.id.slice(5))
  const groupHasPartners = data.some((row) => row.groupId === checkColumn)

  return (
    <Typography
      variant="inherit"
      color={groupHasPartners ? 'primary' : 'error'}
    >
      {column.name}
    </Typography>
  )
}

const GroupingTableTop = ({ partnersWithNoGroup }) => {
  const { t } = useTranslation()

  const theme = useTheme()

  return (
    <Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        style={{ margin: theme.spacing(2) }}
      >
        <Stack direction="row">
          <Typography color="textSecondary">
            {t('rules.grouping.partnerGroups')}
          </Typography>
          <Box mr={2} />
          <DotCell label={t('rules.grouping.status.active')} color="primary" />
          <Box mr={2} />
          <DotCell label={t('rules.grouping.status.inactive')} color="error" />
        </Stack>

        {partnersWithNoGroup > 0 && (
          <Stack direction="row">
            <Typography color="textSecondary">
              {t('rules.grouping.partnersWithoutGroup')}
            </Typography>
            <Typography color="error" style={{ marginLeft: theme.spacing() }}>
              {partnersWithNoGroup}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Divider />
    </Stack>
  )
}

const updateKeys = {
  cost: 'cost',
  restoration: 'restoration',
  useAvailabilityAt: 'use-availability-at',
  maxPrice: 'max-price',
  previewDays: 'preview-days',
  withdrawalPenaltyFreeOfChargeDays: 'withdrawal-penalty-free-of-charge-days',
  withdrawalPenaltyCostPerDay: 'withdrawal-penalty-cost-per-day',
  withdrawalPenaltyMax: 'withdrawal-penalty-max',
  feePreviewFranchiseDays: 'fee-preview-franchise-days',
  feePreviewCostPerDay: 'fee-preview-cost-per-day',
  feeParkedCostPerDay: 'fee-parked-cost-per-day',
  restitutionPenaltyCostPerDay: 'restitution-penalty-cost-per-day',
  restitutionPenaltyExtraPreviewDays: 'restitution-penalty-extra-preview-days',
  restitutionPenaltyExtraPreviewPenaltyDays:
    'restitution-penalty-extra-preview-penalty-days',
  useParkedStatus: 'use-parked-status',
  useBlindPublication: 'use-blind-publication',
  useResoTecnico: 'use-reso-tecnico',
  resoTecnicoDayLimit: 'reso-tecnico-day-limit',
  useRelease: 'use-release',
}

const updateTableValueNoDebounce = async (
  partnerId,
  newValue,
  type,
  showSnackbarMessage,
  refetch,
  t
) => {
  const typeName = t(`rules.table.${type}`)
  try {
    await axios.patch(
      `${config.apiBaseUrl}partners/${partnerId}/${updateKeys[type]}`,
      {
        [type]: newValue,
      }
    )

    showSnackbarMessage({
      severity: 'success',
      message: `${typeName} aggiornato con successo`,
    })
    refetch()
  } catch (e) {
    showSnackbarMessage({
      severity: 'error',
      message: `Errore nell'aggiornamento di ${typeName}`,
    })
  }
}

const updateTableValue = debounce(updateTableValueNoDebounce, 1000)

const RulesBooleanInput = ({ value, row, refetch, type, disabled }) => {
  const { showSnackbarMessage } = useSnackbar()
  const { t } = useTranslation()
  const [newValue, setNewValue] = useState(null)
  const partnerId = row.original.partnerId

  useEffect(() => {
    if (newValue !== null) {
      updateTableValue(
        partnerId,
        newValue,
        type,
        showSnackbarMessage,
        refetch,
        t
      )
    }
  }, [newValue, partnerId, type, refetch, showSnackbarMessage, t])

  return (
    <Switch
      data-testid={`${updateKeys[type]}-switch-${row.original.partnerId}`}
      name={t(`rules.table.${type}`)}
      onChange={(e) => setNewValue(e.target.checked)}
      checked={newValue !== null ? newValue : value}
      disabled={disabled}
    />
  )
}

const RulesNumberInput = ({
  value,
  row,
  refetch,
  type = 'cost',
  disabled,
  canBeEmpty = false,
  onlyPositive = false,
  int = false,
  max = undefined,
  min = undefined,
}) => {
  const { showSnackbarMessage } = useSnackbar()
  const { t } = useTranslation()
  const [localValue, setLocalValue] = useState(value)
  const partnerId = row.original.partnerId

  const handleChange = (event) => {
    if (event.target.value === null || event.target.value === '') {
      setLocalValue(canBeEmpty ? null : value)
      return
    }
    if (onlyPositive && Number(event.target.value) < 0) {
      setLocalValue(0)
      return
    }
    if (typeof max !== 'undefined' && Number(event.target.value) > max) {
      setLocalValue(max)
      return
    }
    if (typeof min !== 'undefined' && Number(event.target.value) < min) {
      setLocalValue(min)
      return
    }

    setLocalValue(
      int && event.target.value !== ''
        ? Math.floor(event.target.value)
        : event.target.value
    )
  }

  const handleBlur = (event) => {
    handleChange(event)

    if (
      ((localValue === null || localValue === '') && value === null) ||
      Number(localValue) === Number(value)
    ) {
      return
    }
    updateTableValueNoDebounce(
      partnerId,
      localValue,
      type,
      showSnackbarMessage,
      refetch,
      t
    )
  }

  useEffect(() => {
    setLocalValue(value)
  }, [value])
  // useEffect(() => {
  //   if (localValue) {
  //     updateTableValue(
  //       partnerId,
  //       localValue,
  //       type,
  //       showSnackbarMessage,
  //       refetch,
  //       t
  //     )
  //   }
  // }, [localValue, partnerId, type, refetch, showSnackbarMessage, t])

  return (
    <TextField
      data-testid={`${updateKeys[type]}-text-${row.original.partnerId}`}
      size="small"
      placeholder={t(`rules.table.${type}`)}
      type="number"
      name={t(`rules.table.${type}`)}
      value={localValue !== null ? localValue : ''}
      onChange={handleChange}
      onBlur={handleBlur}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <CreateIcon />
          </InputAdornment>
        ),
      }}
      disabled={disabled}
    />
  )
}

// Snapshot di sessioni passate: i flag introdotti dopo quella sessione non
// esistono nel payload → '-' come le colonne numeriche, non la chiave i18n.
const readOnlyBooleanCell = (t) => (props) =>
  typeof props?.value === 'boolean' ? t(`rules.switch.${props.value}`) : '-'

const RulesContent = ({
  selectedSessionId,
  disableEditsGroup,
  disableEditsCost,
  disableEditsParameters,
  isCurrentSessionSelected,
  contentIsReadOnly = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedPartner, setSelectedPartner] = useState()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [filteredItems, setFilteredItems] = useState([])

  const query = useQuery()
  const listType = query.get('type') || 'grouping'
  const { t } = useTranslation()

  const {
    filters,
    filtersData,
    setPartners,
    setPartnersId,
    setPartnerGroup,
    setSort,
    resetFilters,
    initFilters,
    filter,
  } = useRulesFilters()

  const { items, isFetching, isFetchingOtherPages, refetch } = useEntityData({
    limit: 9999,
    entity: isCurrentSessionSelected
      ? 'partners'
      : `sessions/${selectedSessionId}/partners`,
  })

  const onMenuClick = useCallback((event, partner) => {
    setAnchorEl(event.currentTarget)
    setSelectedPartner(partner)
  }, [])

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const onEditClick = () => {
    setDialogOpen(true)
  }
  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedPartner()
  }

  const sortedItems = useMemo(
    () =>
      orderBy(
        filteredItems,
        [filters?.order?.column],
        [filters?.order?.direction]
      ),
    [filters?.order?.column, filters?.order?.direction, filteredItems]
  )

  const {
    page,
    rowsPerPage,
    pagedItems,
    setPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useTablePaginationSync({ items: sortedItems })

  useEffect(() => {
    setPage(0)
  }, [listType, resetFilters, selectedSessionId, setPage])

  useEffect(() => {
    resetFilters()
  }, [listType, resetFilters, selectedSessionId])

  useEffect(() => {
    if (!items.length || filters?.initialized) {
      return
    }
    initFilters(items)
  }, [items, initFilters, filters?.initialized])

  const handleFilters = useDebouncedCallback(
    (items, filters, setFilteredItems, filter) => {
      const localItems = filter(items, filters).map((item) => ({
        ...item,
        group1: item.groupId === 1,
        group2: item.groupId === 2,
        group3: item.groupId === 3,
        group4: item.groupId === 4,
        group5: item.groupId === 5,
        group6: item.groupId === 6,
        group7: item.groupId === 7,
        group8: item.groupId === 8,
        group9: item.groupId === 9,
        group10: item.groupId === 10,
      }))

      return setFilteredItems(localItems)
    },
    100
  )

  useEffect(() => {
    handleFilters(items, filters, setFilteredItems, filter)
  }, [filter, filters, handleFilters, items])

  const partnersWithNoGroup = items.reduce((acc, partner) => {
    return partner.groupId ? acc : acc + 1
  }, 0)

  const allOption = useMemo(
    () => getOption(t('common.form.all'), OPTION_ALL_VALUE),
    [t]
  )

  const groups = useMemo(() => createGroups(t), [t])

  const partnerColumnLabel = useMemo(() => t('rules.table.partnerColumn'), [t])

  const partnerIdColumnLabel = useMemo(
    () => t('rules.table.partnerIdColumn'),
    [t]
  )

  const partnerGroupColumnLabel = useMemo(
    () => t('rules.table.partnerGroupColumn'),
    [t]
  )

  const groupingColumns = useMemo(() => {
    const internalCols = [
      {
        accessor: 'partnerGroup',
        Header: (
          <TableHeaderCell
            label={partnerGroupColumnLabel}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.partnersGroup}
                value={filters.partnersGroup}
                handleChange={setPartnerGroup}
                multiple
                hasSearchField
              />
            }
          />
        ),
        sticky: 'left',
        width: 130,
      },
      {
        accessor: 'name',
        Header: (
          <TableHeaderCell
            label={partnerColumnLabel}
            errorsNumber={partnersWithNoGroup}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.partners}
                value={filters.partners}
                handleChange={setPartners}
                multiple
                hasSearchField
              />
            }
          />
        ),
        Cell: ({ value, row }) => {
          return (
            <Typography
              variant="inherit"
              color={row.original.groupId ? 'primary' : 'error'}
            >
              {value}
            </Typography>
          )
        },
        sticky: 'left',
      },
      {
        accessor: 'partnerId',
        Header: (
          <TableHeaderCell
            label={partnerIdColumnLabel}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.partnersId}
                value={filters.partnersId}
                handleChange={setPartnersId}
                multiple
                hasSearchField
              />
            }
          />
        ),
        sticky: 'left',
      },
    ]

    groups.forEach((group) => {
      internalCols.push({
        name: group.name,
        accessor: `group${group.id}`,
        Header: GroupHeaderRenderer,
        Cell: ({ value, column }) => {
          if (!value) return null
          return (
            <DotCell
              label={t('rules.grouping.status.active')}
              color="primary"
            />
          )
        },
      })
    })

    if (!disableEditsGroup) {
      internalCols.push({
        accessor: 'options',
        // eslint-disable-next-line react/display-name
        Cell: (props) => {
          if (!!contentIsReadOnly) {
            return <></>
          } else {
            return (
              <IconButton
                data-testid={`options-${props.row.original.partnerId}`}
                aria-label="More"
                onClick={(e) => onMenuClick(e, props.row.original)}
              >
                <MoreIcon />
              </IconButton>
            )
          }
        },
        width: 70,
      })
    }

    return internalCols
  }, [
    t,
    partnersWithNoGroup,
    partnerColumnLabel,
    partnerIdColumnLabel,
    partnerGroupColumnLabel,
    allOption,
    filtersData.partners,
    filtersData.partnersId,
    filtersData.partnersGroup,
    filters.partners,
    filters.partnersId,
    filters.partnersGroup,
    setPartners,
    setPartnersId,
    setPartnerGroup,
    groups,
    disableEditsGroup,
    onMenuClick,
    contentIsReadOnly,
  ])

  const costingColumns = useMemo(() => {
    return [
      {
        accessor: 'partnerGroup',
        Header: (
          <TableHeaderCell
            label={partnerGroupColumnLabel}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.partnersGroup}
                value={filters.partnersGroup}
                handleChange={setPartnerGroup}
                multiple
                hasSearchField
              />
            }
          />
        ),
      },
      {
        accessor: 'name',
        Header: (
          <TableHeaderCell
            label={partnerColumnLabel}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.partners}
                value={filters.partners}
                handleChange={setPartners}
                multiple
                hasSearchField
              />
            }
          />
        ),
        cellProps: {
          width: 200,
        },
      },
      {
        accessor: 'partnerId',
        Header: (
          <TableHeaderCell
            label={partnerIdColumnLabel}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.partnersId}
                value={filters.partnersId}
                handleChange={setPartnersId}
                multiple
                hasSearchField
              />
            }
          />
        ),
      },
      {
        accessor: 'cost',
        Header: t('rules.table.cost'),
        ...(!disableEditsCost && {
          Cell: (props) => {
            return (
              <RulesNumberInput
                {...props}
                refetch={refetch}
                disabled={contentIsReadOnly}
              />
            )
          },
        }),
      },
    ]
  }, [
    partnerColumnLabel,
    partnerGroupColumnLabel,
    allOption,
    filtersData.partners,
    filtersData.partnersId,
    filtersData.partnersGroup,
    filters.partners,
    filters.partnersId,
    filters.partnersGroup,
    setPartners,
    partnerIdColumnLabel,
    setPartnersId,
    setPartnerGroup,
    t,
    disableEditsCost,
    refetch,
    contentIsReadOnly,
  ])

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const parametersColumns = useMemo(() => {
    return [
      {
        accessor: 'partnerGroup',
        Header: (
          <TableHeaderCell
            label={partnerGroupColumnLabel}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.partnersGroup}
                value={filters.partnersGroup}
                handleChange={setPartnerGroup}
                multiple
                hasSearchField
              />
            }
          />
        ),
        sticky: 'left',
        width: 130,
      },
      {
        accessor: 'name',
        Header: (
          <TableHeaderCell
            label={partnerColumnLabel}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.partners}
                value={filters.partners}
                handleChange={setPartners}
                multiple
                hasSearchField
              />
            }
          />
        ),
        cellProps: {
          width: 200,
        },
        sticky: 'left',
        width: 200,
      },
      {
        accessor: 'restoration',
        Header: t('rules.table.restoration'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesBooleanInput
                    type="restoration"
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: readOnlyBooleanCell(t) }),
      },
      {
        accessor: 'useAvailabilityAt',
        Header: t('rules.table.useAvailabilityAt'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesBooleanInput
                    type="useAvailabilityAt"
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: readOnlyBooleanCell(t) }),
      },
      {
        accessor: 'useParkedStatus',
        Header: t('rules.table.useParkedStatus'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesBooleanInput
                    type="useParkedStatus"
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: readOnlyBooleanCell(t) }),
      },
      {
        accessor: 'useResoTecnico',
        Header: t('rules.table.useResoTecnico'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesBooleanInput
                    type="useResoTecnico"
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: readOnlyBooleanCell(t) }),
      },
      {
        accessor: 'useBlindPublication',
        Header: t('rules.table.useBlindPublication'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesBooleanInput
                    type="useBlindPublication"
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: readOnlyBooleanCell(t) }),
      },
      {
        accessor: 'useRelease',
        Header: t('rules.table.useRelease'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesBooleanInput
                    type="useRelease"
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: readOnlyBooleanCell(t) }),
      },
      {
        accessor: 'maxPrice',
        Header: t('rules.table.maxPrice'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesNumberInput
                    int
                    type="maxPrice"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
      {
        accessor: 'previewDays',
        Header: t('rules.table.previewDays'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesNumberInput
                    int
                    type="previewDays"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
      {
        accessor: 'resoTecnicoDayLimit',
        Header: t('rules.table.resoTecnicoDayLimit'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesNumberInput
                    int
                    type="resoTecnicoDayLimit"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
      {
        accessor: 'withdrawalPenaltyFreeOfChargeDays',
        Header: t('rules.table.withdrawalPenaltyFreeOfChargeDays'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesNumberInput
                    int
                    type="withdrawalPenaltyFreeOfChargeDays"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
      {
        accessor: 'withdrawalPenaltyCostPerDay',
        Header: t('rules.table.withdrawalPenaltyCostPerDay'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesNumberInput
                    type="withdrawalPenaltyCostPerDay"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
      {
        accessor: 'withdrawalPenaltyMax',
        Header: t('rules.table.withdrawalPenaltyMax'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesNumberInput
                    type="withdrawalPenaltyMax"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
      {
        accessor: 'feePreviewFranchiseDays',
        Header: t('rules.table.feePreviewFranchiseDays'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesNumberInput
                    int
                    type="feePreviewFranchiseDays"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
      {
        accessor: 'feePreviewCostPerDay',
        Header: t('rules.table.feePreviewCostPerDay'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesNumberInput
                    type="feePreviewCostPerDay"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
      {
        accessor: 'feeParkedCostPerDay',
        Header: t('rules.table.feeParkedCostPerDay'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                const disableCell = props.row.values.useParkedStatus === false

                return (
                  <RulesNumberInput
                    type="feeParkedCostPerDay"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly || disableCell}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
      {
        accessor: 'restitutionPenaltyCostPerDay',
        Header: t('rules.table.restitutionPenaltyCostPerDay'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesNumberInput
                    type="restitutionPenaltyCostPerDay"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
      {
        accessor: 'restitutionPenaltyExtraPreviewDays',
        Header: t('rules.table.restitutionPenaltyExtraPreviewDays'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesNumberInput
                    int
                    type="restitutionPenaltyExtraPreviewDays"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
      {
        accessor: 'restitutionPenaltyExtraPreviewPenaltyDays',
        Header: t('rules.table.restitutionPenaltyExtraPreviewPenaltyDays'),
        ...(!disableEditsParameters
          ? {
              Cell: (props) => {
                return (
                  <RulesNumberInput
                    int
                    type="restitutionPenaltyExtraPreviewPenaltyDays"
                    onlyPositive
                    {...props}
                    refetch={refetch}
                    disabled={contentIsReadOnly}
                  />
                )
              },
            }
          : { Cell: (props) => props?.value ?? '-' }),
      },
    ]
  }, [
    t,
    allOption,
    partnerColumnLabel,
    partnerGroupColumnLabel,
    filtersData.partners,
    filtersData.partnersGroup,
    filters.partners,
    filters.partnersGroup,
    setPartners,
    setPartnerGroup,
    disableEditsParameters,
    refetch,
    contentIsReadOnly,
  ])

  return (
    <>
      <RulesHeader listType={listType} />

      <Table
        top={
          listType === 'grouping' && (
            <GroupingTableTop partnersWithNoGroup={partnersWithNoGroup} />
          )
        }
        items={pagedItems}
        columns={
          listType === 'grouping'
            ? groupingColumns
            : listType === 'costing'
            ? costingColumns
            : parametersColumns
        }
        isFetching={isFetching}
        isFetchingOtherPages={isFetchingOtherPages}
        total={filteredItems.length}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        hasPagination={true}
        rowsPerPageOptions={[10, 20, 30, 50]}
        sortable
        orderColumn={filters?.order?.column}
        orderDirection={filters?.order?.direction}
        handleSort={setSort}
      />

      <Menu
        data-testid={`action-menu`}
        id="options-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleCloseMenu}
      >
        <MenuItem
          data-testid={`action-menu-edit`}
          onClick={() => {
            if (contentIsReadOnly === false) {
              onEditClick()
              handleCloseMenu()
            }
          }}
        >
          {t('rules.menu.edit')}
        </MenuItem>
      </Menu>

      <GroupAssignmentDialog
        open={dialogOpen}
        partner={selectedPartner}
        handleClose={handleCloseDialog}
        refetch={refetch}
      />
    </>
  )
}

RulesContent.propTypes = {
  selectedSessionId: T.string,
  disableEditsGroup: T.bool,
  disableEditsCost: T.bool,
  disableEditsParameters: T.bool,
  isCurrentSessionSelected: T.bool,
  contentIsReadOnly: T.bool,
}

export default RulesContent
